"use client";

import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@/components/ui/pagination";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

interface PaginationProps {
  totalPages: number;
  onPageChange: (page: number) => void;
}

export default function CustomPagination({
  totalPages,
  onPageChange,
}: PaginationProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const page = Number(searchParams.get("page")) || 1;
  // Clamp the page from URL so it never exceeds totalPages
  const safePage = Math.max(1, Math.min(page, totalPages || 1));
  const [currentPage, setCurrentPage] = useState(safePage);

  useEffect(() => {
    setCurrentPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage, totalPages]);

  const goToPage = (pageNumber: number) => {
    // Clamp so we never go out of bounds
    const clamped = Math.max(1, Math.min(pageNumber, totalPages));
    if (clamped === currentPage) return;

    setCurrentPage(clamped);
    onPageChange(clamped);

    const params = new URLSearchParams(searchParams);
    params.set("page", String(clamped));
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  const getPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];
    pageNumbers.push(1);

    if (currentPage > 3) {
      pageNumbers.push("ellipsis1");
    }

    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      if (i !== 1 && i !== totalPages) {
        pageNumbers.push(i);
      }
    }

    if (currentPage < totalPages - 2) {
      pageNumbers.push("ellipsis2");
    }

    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  return (
    <>
      {totalPages > 1 && (
        <Pagination>
          <PaginationContent className="p-4 sm:p-6 flex items-center justify-center gap-1 sm:gap-2">
            <PaginationItem>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className={`p-1.5 sm:p-2 border border-[#D0C4FF] rounded-full transition-all ${
                  currentPage <= 1
                    ? "bg-gray-100 opacity-50 cursor-not-allowed"
                    : "bg-white cursor-pointer hover:bg-gray-50"
                }`}
              >
                <ChevronLeft className="size-5 sm:size-6 md:size-7 lg:size-8 xl:size-9" />
              </button>
            </PaginationItem>

            {getPageNumbers().map((page, index) => {
              if (page === "ellipsis1" || page === "ellipsis2") {
                return (
                  <PaginationItem key={`ellipsis-${index}`}>
                    <span className="mx-0.5 sm:mx-1 text-sm sm:text-base">...</span>
                  </PaginationItem>
                );
              }

              const isActive = page === currentPage;
              return (
                <PaginationItem key={`page-${page}`}>
                  <button
                    onClick={() => goToPage(Number(page))}
                    className="cursor-pointer relative flex items-center justify-center mx-0.5 sm:mx-3 font-normal text-sm md:text-base ff_poppins"
                  >
                    <span
                      className={`size-[36px] sm:size-[45px] lg:size-[52px] xl:size-[55px] 3xl:size-[60px] rounded-full flex items-center justify-center transition-all border border-[#D0C4FF] ${isActive ? "bg-primary text-white font-bold" : "!bg-white text-black font-semibold"}`}
                    >
                      {page}
                    </span>
                  </button>
                </PaginationItem>
              );
            })}

            <PaginationItem>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className={`p-1.5 sm:p-2 border border-[#D0C4FF] rounded-full transition-all ${
                  currentPage >= totalPages
                    ? "bg-gray-100 opacity-50 cursor-not-allowed"
                    : "bg-white cursor-pointer hover:bg-gray-50"
                }`}
              >
                <ChevronRight className="size-5 sm:size-6 md:size-7 lg:size-8 xl:size-9" />
              </button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
}
